import React from "react";
import InlineShortcutAssigner from "@/components/InlineShortcutAssigner";
import { useShortcut } from "@/context/ShortcutsContext";

/**
 * v1.29.12 — Page-level shortcut header (title block only).
 *
 * Renders the "left" title block that every standalone Admin page uses
 * (eyebrow kicker + display headline) and appends the currently-assigned
 * shortcut badge plus the `+`/pencil assigner so Admins and Super Users
 * can bind or rebind a keyboard shortcut for that page. Reads live from
 * the MongoDB `app_shortcuts` collection via `useShortcut(action_id)` —
 * no reload needed after a save.
 *
 * The component intentionally does NOT own the outer flex-between layout;
 * pages compose it next to their existing right-side action rows (export
 * buttons, filters, refresh, etc.) so nothing shifts visually beyond the
 * new badge + `+` icon next to the title.
 *
 * Usage:
 *   <PageShortcutHeader
 *       action_id="page:payment-slips"
 *       label="Payment Slips"
 *       target_route="/admin/uploads"
 *       eyebrow="Verification Queue"
 *       title="Payment Slips"
 *   />
 */
export default function PageShortcutHeader({
    action_id,
    label,
    target_route,
    title,
    subtitle,
    eyebrow,
    titleClassName = "font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl",
    subtitleClassName = "mt-2 text-sm text-slate-500",
    category = "Page",
}) {
    const current = useShortcut(action_id);
    return (
        <div data-testid={`page-header-${action_id}`}>
            {eyebrow && (
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                    {eyebrow}
                </div>
            )}
            <div className={`${eyebrow ? "mt-1 " : ""}flex flex-wrap items-center gap-2`}>
                <h1 className={titleClassName}>{title || label}</h1>
                {current?.combo && (
                    <span
                        data-testid={`page-shortcut-badge-${action_id}`}
                        title={`${current.combo} · Keyboard shortcut for this page`}
                        className="inline-flex items-center rounded-md border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-700 shadow-sm"
                    >
                        {current.combo}
                    </span>
                )}
                <span title="Assign Keyboard Shortcut">
                    <InlineShortcutAssigner
                        action_id={action_id}
                        label={label}
                        target_route={target_route || ""}
                        category={category}
                    />
                </span>
            </div>
            {subtitle && <p className={subtitleClassName}>{subtitle}</p>}
        </div>
    );
}
